import { yupResolver } from "@hookform/resolvers/yup";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Button as MUIButton } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { getConfigs } from "app/reducers/configSlice";
import { fetchConfigs, getConfigById } from "app/thunks/configAPI";
import { createMatch } from "app/thunks/matchesAPI";
import classNames from "classnames";
import { SESSION_STATUS } from "interfaces/Sessions";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { BUTTON_STYLES, BUTTON_TYPES, Spinner, useCustomToast } from "ui";
import { Input } from "ui/Form";
import { Select } from "ui/Form/Select";

import { playerSchema } from "./validations";

export type CreateMatchData = {
  sessionId: string | null;
  containerId: string | null;
  playerAlias: string;
};

export const AddPlayerForm = () => {
  const dispatch = useAppDispatch();
  const {
    configs,
    isFetching: configsFetching,
    config,
    isLoading,
  } = useAppSelector(getConfigs);
  const [isAdding, setIsAdding] = useState(false);
  const { showErrorToast } = useCustomToast();

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors, isValid },
    control,
    reset,
    setValue,
  } = useForm<CreateMatchData>({
    mode: "all",
    resolver: yupResolver(playerSchema),
    defaultValues: {
      sessionId: null,
      containerId: null,
      playerAlias: "",
    },
  });

  useEffect(() => {
    dispatch(fetchConfigs());
  }, [dispatch, reset]);

  const sessionValue = watch("sessionId");
  const containerValue = watch("containerId");

  const sessionOptions = useMemo(() => {
    if (configs) {
      return configs
        .filter(
          ({ sessions }) =>
            sessions[0]?.status &&
            (sessions[0]?.status === SESSION_STATUS.STANDBY ||
              sessions[0]?.status === SESSION_STATUS.READY),
        )
        .map(({ sessionNumber, sessions }) => ({
          value: sessions[0]?._id,
          label: sessionNumber,
        }));
    }
    return [];
  }, [configs]);

  const containersOptions = useMemo(() => {
    if (config && sessionValue) {
      return config.containerSlots
        .filter((containerSlot) => containerSlot.slotId.deviceId !== null)
        .map(({ containerId }) => ({
          value: containerId?._id,
          label: `Contenedor ${containerId.number}`,
        }));
    }
    return [];
  }, [config, sessionValue]);

  useEffect(() => {
    if (configsFetching) return reset();
    if (sessionValue) {
      const configId = configs.find(
        ({ sessions }) => sessions[0]?._id === sessionValue,
      )?._id;

      configId && dispatch(getConfigById(configId));
    }
  }, [configs, configsFetching, dispatch, reset, sessionValue]);

  const onSubmit = async (data: CreateMatchData) => {
    try {
      setIsAdding(true);
      await dispatch(createMatch(data)).unwrap();
      reset();
    } catch (_) {
      showErrorToast({
        title: "Error",
        message: "Ha ocurrido un error. Por favor intente nuevamente.",
      });
    } finally {
      setIsAdding(false);
    }
  };

  if (configsFetching) {
    return (
      <div className="flex w-full justify-center items-center bg-dark-gray-blue h-screen">
        <Spinner size={60} />
      </div>
    );
  }

  return (
    <>
      <form
        className="flex flex-col px-5 justify-between flex-1"
        onSubmit={handleSubmit(onSubmit)}
      >
        <h1 className="mt-12 mb-6 text-xl text-white capitalize">
          Agregar jugador
        </h1>
        <div className="flex mb-6">
          <div className="flex items-center pr-2.5">
            <WarningAmberIcon
              fontSize="large"
              className="text-yellow-400"
              aria-hidden="true"
            />
          </div>
          <p>
            Para agregar un jugador, asegúrese de que la sesión este comenzada
          </p>
        </div>
        <Controller
          name="sessionId"
          control={control}
          render={({ field: { onChange, ...rest } }) => (
            <Select
              disableOptionMap
              label="Sesión"
              placeholder="Sesión"
              aria-label="Sessions Select"
              options={sessionOptions}
              disabled={!sessionOptions.length}
              onChange={(e) => {
                onChange(e);
                setValue("containerId", null);
                setValue("playerAlias", "");
              }}
              {...rest}
            />
          )}
        />
        <Controller
          name="containerId"
          control={control}
          render={({ field }) => (
            <Select
              disableOptionMap
              label="Contenedor"
              placeholder="Contenedor"
              aria-label="Container Select"
              options={containersOptions}
              disabled={!containersOptions.length || !sessionValue}
              isLoading={isLoading}
              {...field}
            />
          )}
        />
        {sessionValue && config && !isLoading && !containersOptions.length && (
          <div className="flex">
            <div className="flex items-center pr-2.5">
              <WarningAmberIcon
                fontSize="small"
                className="text-yellow-400"
                aria-hidden="true"
              />
            </div>
            <p className="text-xs mt-1 pb-2">
              No existen más contenedores disponibles para la sesión elegida
            </p>
          </div>
        )}
        <Input
          {...register("playerAlias")}
          type="text"
          placeholder="Ingresa el alias"
          label="Alias"
          errorMessage={errors.playerAlias?.message}
          disabled={!containerValue}
        />
        <div className="flex flex-col self-center w-7/12 mt-auto mb-10 space-y-3">
          <MUIButton
            variant="contained"
            disabled={!isValid}
            className={classNames(BUTTON_STYLES[BUTTON_TYPES.CONFIRM])}
            type="submit"
          >
            {isAdding ? <Spinner color="white" /> : <p>Guardar</p>}
          </MUIButton>
        </div>
      </form>
    </>
  );
};
